// Helpers for working with the new per-client StaffPlan artifact.

import type {
  ClientProfile,
  DisciplineChannelCell,
  LevelId,
  Model,
  Scenario,
  ScenarioWorkspace,
  StaffPlan,
  StaffPlanCell,
  StaffPlanCellOverride,
  StaffPlanEntrySource,
  StaffPlanHubLocation,
} from '../types.js';
import { lookupCostPerRole } from './table-lookup.js';
import { splitShore } from './shore-split.js';
import { exactLevelLabel } from './level-groups.js';

/** Key used to address a staff-plan row (discipline × channel). */
export function staffPlanRowKey(disciplineId: string, channelId: string): string {
  return `${disciplineId}::${channelId}`;
}

/** Key used to address a single modeled cell (discipline × channel × level),
 *  which is the grain manual overrides are stored at. */
export function staffPlanCellKey(
  disciplineId: string,
  channelId: string,
  levelId: LevelId | string,
): string {
  return `${disciplineId}::${channelId}::${levelId}`;
}

/** A plan may back a scenario's cost-savings comparison only when it records
 *  the client's real team. Plans saved before `kind` existed were all
 *  hand-entered, so a missing `kind` reads as 'actual'. */
export function isActualStaffPlan(
  plan: Pick<StaffPlan, 'kind'> | undefined | null,
): boolean {
  if (!plan) return false;
  return (plan.kind ?? 'actual') === 'actual';
}

/** How an actual plan was entered. Legacy / missing → manual. */
export function staffPlanEntrySource(
  plan: Pick<StaffPlan, 'entrySource'> | undefined | null,
): StaffPlanEntrySource {
  return plan?.entrySource === 'uploaded' ? 'uploaded' : 'manual';
}

export function staffPlanEntrySourceLabel(
  plan: Pick<StaffPlan, 'entrySource'> | undefined | null,
): string {
  return staffPlanEntrySource(plan) === 'uploaded' ? 'Upload' : 'Manual input';
}

/** Which editor a scenario opens in. Legacy / missing → advanced. */
export function scenarioWorkspace(
  scenario: Pick<Scenario, 'workspace'> | null | undefined,
): ScenarioWorkspace {
  return scenario?.workspace === 'basic' ? 'basic' : 'advanced';
}

/**
 * Is a stored hub reference usable against a given model? A hub with no
 * `hubId` is a shore-only hint (legacy/custom) and is always accepted; a hub
 * that names a `hubId` is only valid when that id still exists in the model
 * being costed. `validHubIds === undefined` disables the check (accept all).
 */
export function isHubValid(
  hub: StaffPlanHubLocation | undefined,
  validHubIds?: ReadonlySet<string>,
): boolean {
  if (!hub) return false;
  if (!hub.hubId) return true;
  if (!validHubIds) return true;
  return validHubIds.has(hub.hubId);
}

/**
 * Resolve the hub location that applies to a given (discipline × channel) row.
 * Precedence: the row's own override in `plan.rowHubs`, then the plan-wide
 * `hubLocation` default, then `undefined` (callers treat this as offshore).
 *
 * Pass `validHubIds` (e.g. the id set of the model being costed) to harden
 * against model drift: a stored hub whose `hubId` no longer exists in the
 * model is skipped so we fall back to the next valid option rather than
 * costing against a dangling hub. This keeps a staff plan authored under one
 * model from breaking when consumed by a scenario on a different model.
 */
export function resolveRowHub(
  plan: Pick<StaffPlan, 'hubLocation' | 'rowHubs'> | undefined,
  disciplineId: string,
  channelId: string,
  validHubIds?: ReadonlySet<string>,
): StaffPlanHubLocation | undefined {
  if (!plan) return undefined;
  const override = plan.rowHubs?.[staffPlanRowKey(disciplineId, channelId)];
  if (isHubValid(override, validHubIds)) return override;
  if (isHubValid(plan.hubLocation, validHubIds)) return plan.hubLocation;
  return undefined;
}

/** Resolve a cell's effective Total / Onshore / Hub headcount.
 *  - Total comes from `fte` (the source of truth maintained by the editor).
 *  - When explicit `onFte` / `hubFte` are present, they are used as-is.
 *  - When absent (legacy cells) we treat the headcount as 100% onshore, which
 *    preserves backwards compatibility with plans saved before the on/hub
 *    split was introduced. */
export function resolveCellSplit(c: Pick<StaffPlanCell, 'fte' | 'onFte' | 'hubFte'>): {
  total: number;
  on: number;
  hub: number;
} {
  const total = Number(c.fte) || 0;
  const hasOn = typeof c.onFte === 'number';
  const hasHub = typeof c.hubFte === 'number';
  if (hasOn && hasHub) {
    const on = Number(c.onFte) || 0;
    const hub = Number(c.hubFte) || 0;
    return { total: on + hub, on, hub };
  }
  if (hasHub) {
    const hub = Number(c.hubFte) || 0;
    return { total, on: Math.max(0, total - hub), hub };
  }
  if (hasOn) {
    const on = Number(c.onFte) || 0;
    return { total, on, hub: Math.max(0, total - on) };
  }
  return { total, on: total, hub: 0 };
}

/**
 * Collapse a staff plan's discipline × channel × level cells into the legacy
 * `disciplineId:levelId` keyed map that the engine consumes via
 * `ClientProfile.currentStaffByLevel`.
 */
export function flattenStaffPlanToByLevel(
  cells: StaffPlanCell[] | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!cells?.length) return out;
  for (const c of cells) {
    if (!c.disciplineId || !c.levelId) continue;
    const { total } = resolveCellSplit(c);
    if (!total) continue;
    const key = `${c.disciplineId}:${c.levelId}`;
    out[key] = (out[key] || 0) + total;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Modeled-Adjusted — the modeled grid with manual per-cell FTE pinned on top.
//
// The engine sizes staffing from budget and model tables; these helpers layer
// hand edits over that output without touching the pipeline. Adjustment lives
// downstream of `runCalculation`, so the five sizing stages, `/api/calculate`
// and the client-side recompute are all unaffected.
// ---------------------------------------------------------------------------

/** Tolerance for "the model moved". 1 FTE is ~1800 hours, so 1e-4 FTE is well
 *  under a fifth of an hour a year — float noise, not a real change. */
const DRIFT_EPS = 1e-4;

/** A cell whose modeled baseline has moved since the user pinned it. Carries
 *  all three numbers so the UI can show what was, what is, and what the user
 *  asked for. */
export interface StaffPlanCellDrift {
  key: string;
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  levelId: LevelId;
  levelLabel: string;
  /** What the user pinned. */
  onFte: number;
  hubFte: number;
  /** What the model said when the edit was made. */
  baselineOnFte: number;
  baselineHubFte: number;
  /** What the model says now. */
  modeledOnFte: number;
  modeledHubFte: number;
}

export interface AdjustedStaffPlan {
  /** The modeled grid with overrides applied. */
  cells: DisciplineChannelCell[];
  /** Overridden cells whose modeled baseline has since moved. */
  drifted: StaffPlanCellDrift[];
  /** Override keys with no matching modeled cell. The model no longer sizes
   *  that discipline × channel × level at all — usually because budget,
   *  leveling or channel activation changed. Those still carrying FTE are
   *  synthesized into `cells` so the number is not silently lost. */
  orphanedKeys: string[];
  /** How many overrides matched a modeled cell and were applied. */
  appliedCount: number;
}

/** Per-FTE rates recovered from a modeled cell, used to re-cost it after the
 *  headcount changes. A shore the model never staffed has no rate of its own,
 *  so it falls back to the cell's blended rate. */
function cellRates(c: DisciplineChannelCell) {
  const total = Number(c.adjustedFte) || 0;
  const blended = total > DRIFT_EPS ? (Number(c.totalCost) || 0) / total : 0;
  const rate = (cost: number, fte: number) =>
    fte > DRIFT_EPS ? cost / fte : blended;
  return {
    onshore: rate(Number(c.onshoreCost) || 0, Number(c.onshoreFte) || 0),
    nearshore: rate(Number(c.nearshoreCost) || 0, Number(c.nearshoreFte) || 0),
    offshore: rate(Number(c.offshoreCost) || 0, Number(c.offshoreFte) || 0),
  };
}

/**
 * Layer manual FTE overrides onto the modeled grid to produce the
 * Modeled-Adjusted plan.
 *
 * Overrides are pinned absolute On/Hub values, so they survive the modeled
 * numbers moving underneath them; the resulting drift is reported rather than
 * resolved, leaving the choice to the user. Each adjusted cell keeps its
 * internal consistency: the Hub headcount is re-split into nearshore /
 * offshore on the modeled ratio, and every cost is re-derived from the cell's
 * own per-FTE rates, so an adjusted cell never carries a headcount from the
 * user and a cost from the model.
 *
 * Pass `model` to resolve labels for orphaned overrides; without it they fall
 * back to raw ids.
 */
export function applyStaffPlanOverrides(
  cells: DisciplineChannelCell[] | undefined,
  overrides: Record<string, StaffPlanCellOverride> | undefined,
  model?: Model,
): AdjustedStaffPlan {
  const source = cells || [];
  const keys = overrides ? Object.keys(overrides) : [];
  if (keys.length === 0) {
    return {
      cells: source,
      drifted: [],
      orphanedKeys: [],
      appliedCount: 0,
    };
  }

  const drifted: StaffPlanCellDrift[] = [];
  const seen = new Set<string>();
  let appliedCount = 0;

  const out = source.map((c) => {
    const key = staffPlanCellKey(c.disciplineId, c.channelId, c.levelId);
    const ov = overrides?.[key];
    if (!ov) return c;
    seen.add(key);
    appliedCount += 1;

    const modeledOn = Number(c.onshoreFte) || 0;
    const modeledNear = Number(c.nearshoreFte) || 0;
    const modeledOff = Number(c.offshoreFte) || 0;
    const modeledHub = modeledNear + modeledOff;

    if (
      Math.abs(modeledOn - (Number(ov.baselineOnFte) || 0)) > DRIFT_EPS ||
      Math.abs(modeledHub - (Number(ov.baselineHubFte) || 0)) > DRIFT_EPS
    ) {
      drifted.push({
        key,
        disciplineId: c.disciplineId,
        disciplineLabel: c.disciplineLabel,
        channelId: c.channelId,
        channelLabel: c.channelLabel,
        levelId: c.levelId,
        levelLabel: c.levelLabel,
        onFte: Number(ov.onFte) || 0,
        hubFte: Number(ov.hubFte) || 0,
        baselineOnFte: Number(ov.baselineOnFte) || 0,
        baselineHubFte: Number(ov.baselineHubFte) || 0,
        modeledOnFte: modeledOn,
        modeledHubFte: modeledHub,
      });
    }

    const nextOn = Math.max(0, Number(ov.onFte) || 0);
    const nextHub = Math.max(0, Number(ov.hubFte) || 0);
    const nextTotal = nextOn + nextHub;

    // Keep the modeled nearshore/offshore ratio. With no modeled hub to learn
    // a ratio from, new hub headcount is treated as offshore, matching
    // `resolveRowHub`'s fallback.
    const nearShare = modeledHub > DRIFT_EPS ? modeledNear / modeledHub : 0;
    const nextNear = nextHub * nearShare;
    const nextOff = nextHub - nextNear;

    const rates = cellRates(c);
    const onshoreCost = nextOn * rates.onshore;
    const nearshoreCost = nextNear * rates.nearshore;
    const offshoreCost = nextOff * rates.offshore;

    const modeledTotal = Number(c.adjustedFte) || 0;
    const scale = modeledTotal > DRIFT_EPS ? nextTotal / modeledTotal : 0;
    const sAutoFte = (Number(c.sAutoFte) || 0) * scale;

    return {
      ...c,
      baseFte: modeledTotal > DRIFT_EPS ? (Number(c.baseFte) || 0) * scale : nextTotal,
      adjustedFte: nextTotal,
      onshoreFte: nextOn,
      nearshoreFte: nextNear,
      offshoreFte: nextOff,
      onshoreCost,
      nearshoreCost,
      offshoreCost,
      totalCost: onshoreCost + nearshoreCost + offshoreCost,
      sAutoFte,
      postAutomationFte: Math.max(0, nextTotal - sAutoFte),
    };
  });

  // Overrides the modeled grid no longer has a cell for. Anything still
  // carrying headcount is synthesized so it keeps counting toward the basis;
  // costs stay at zero because the model has no rates for a cell it does not
  // size.
  const orphanedKeys: string[] = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    orphanedKeys.push(key);
    const ov = overrides?.[key];
    if (!ov) continue;
    const nextOn = Math.max(0, Number(ov.onFte) || 0);
    const nextHub = Math.max(0, Number(ov.hubFte) || 0);
    if (nextOn + nextHub <= DRIFT_EPS) continue;

    const [disciplineId = '', channelId = '', levelId = ''] = key.split('::');
    out.push({
      disciplineId,
      disciplineLabel: labelForDiscipline(model, disciplineId),
      channelId,
      channelLabel: labelForChannel(model, channelId),
      levelId: levelId as LevelId,
      levelLabel: exactLevelLabel(levelId as LevelId),
      baseFte: nextOn + nextHub,
      adjustedFte: nextOn + nextHub,
      onshoreFte: nextOn,
      nearshoreFte: 0,
      offshoreFte: nextHub,
      onshoreCost: 0,
      nearshoreCost: 0,
      offshoreCost: 0,
      totalCost: 0,
      sAutoFte: 0,
      postAutomationFte: nextOn + nextHub,
    });
  }

  return { cells: out, drifted, orphanedKeys, appliedCount };
}

function labelForDiscipline(model: Model | undefined, id: string): string {
  return (model?.disciplines || []).find((d) => d.id === id)?.label || id;
}

function labelForChannel(model: Model | undefined, id: string): string {
  if (id === 'all') return 'All channels';
  return (model?.channels || []).find((c) => c.id === id)?.label || id;
}

/**
 * Project a saved staff plan onto the modeled grid's shape so it can stand in
 * as an automation basis.
 *
 * Hub headcount lands on nearshore or offshore according to the plan's own
 * row hub, falling back to the plan-wide default and then to offshore, which
 * mirrors `resolveRowHub`.
 *
 * Each leg is priced off the model's rate card — the onshore column for the
 * Onshore headcount, the row hub's own shore and hub for the Hub headcount —
 * exactly as the Cost analysis card prices an Actual plan. A hand-entered plan
 * has no engine costing of its own, and the outputs that read a basis need
 * dollars as well as headcount. Without a model there are no rates, so cost
 * stays at zero.
 */
export function staffPlanToBasisCells(
  plan: Pick<StaffPlan, 'cells' | 'hubLocation' | 'rowHubs'> | undefined | null,
  model?: Model,
): DisciplineChannelCell[] {
  if (!plan?.cells?.length) return [];

  // Hubs the model being costed still knows about. A plan authored under a
  // different model may name a hub that is gone here; skipping it falls back to
  // the plan-wide default rather than pricing against a dangling hub.
  const validHubIds = model
    ? new Set((model.ogsHubs || []).map((h) => h.id))
    : undefined;

  // A plan can legitimately hold more than one row for the same grain; sum
  // them so the basis has one cell per discipline × channel × level.
  const byKey = new Map<string, DisciplineChannelCell>();
  for (const c of plan.cells) {
    if (!c.disciplineId || !c.levelId) continue;
    const { total, on, hub } = resolveCellSplit(c);
    if (total <= DRIFT_EPS) continue;

    const rowHub = resolveRowHub(plan, c.disciplineId, c.channelId, validHubIds);
    const shore = rowHub?.shore;
    const nearshore = shore === 'nearshore';
    const near = nearshore ? hub : 0;
    const off = nearshore ? 0 : hub;

    const onCost = model
      ? on *
        (Number(
          lookupCostPerRole(model, 'onshore', c.disciplineId, c.channelId, c.levelId),
        ) || 0)
      : 0;
    const hubCost = model
      ? hub *
        (Number(
          lookupCostPerRole(
            model,
            nearshore ? 'nearshore' : 'offshore',
            c.disciplineId,
            c.channelId,
            c.levelId,
            rowHub?.hubId,
          ),
        ) || 0)
      : 0;

    const key = staffPlanCellKey(c.disciplineId, c.channelId, c.levelId);
    const prev = byKey.get(key);
    if (prev) {
      prev.baseFte += total;
      prev.adjustedFte += total;
      prev.onshoreFte += on;
      prev.nearshoreFte += near;
      prev.offshoreFte += off;
      prev.onshoreCost += onCost;
      prev.nearshoreCost += nearshore ? hubCost : 0;
      prev.offshoreCost += nearshore ? 0 : hubCost;
      prev.totalCost += onCost + hubCost;
      prev.postAutomationFte += total;
      continue;
    }
    byKey.set(key, {
      disciplineId: c.disciplineId,
      disciplineLabel: labelForDiscipline(model, c.disciplineId),
      channelId: c.channelId,
      channelLabel: labelForChannel(model, c.channelId),
      levelId: c.levelId,
      levelLabel: exactLevelLabel(c.levelId),
      baseFte: total,
      adjustedFte: total,
      onshoreFte: on,
      nearshoreFte: near,
      offshoreFte: off,
      onshoreCost: onCost,
      nearshoreCost: nearshore ? hubCost : 0,
      offshoreCost: nearshore ? 0 : hubCost,
      totalCost: onCost + hubCost,
      sAutoFte: 0,
      postAutomationFte: total,
    });
  }
  return Array.from(byKey.values());
}

/**
 * Build automation/Summary basis cells from a staff plan's **total** FTE,
 * re-split On↔Hub via `profile.ogs` / `splitShore` (Basic scenario overlay).
 *
 * Unlike `staffPlanToBasisCells`, the plan's stored on/hub columns are ignored
 * for the split — only totals matter — so changing OGS % in the scenario does
 * not rewrite the saved StaffPlan artifact.
 */
export function staffPlanToOgsAdjustedBasisCells(
  plan: Pick<StaffPlan, 'cells'> | undefined | null,
  model: Model | undefined,
  profile: ClientProfile | undefined | null,
): DisciplineChannelCell[] {
  if (!plan?.cells?.length || !model) return [];
  const profileForSplit: ClientProfile = profile ?? {
    totalBudget: 0,
    channelAllocation: {},
    selections: {},
    currentStaffByLevel: {},
  };

  const byKey = new Map<string, DisciplineChannelCell>();
  for (const c of plan.cells) {
    if (!c.disciplineId || !c.levelId) continue;
    const { total } = resolveCellSplit(c);
    if (total <= DRIFT_EPS) continue;

    const split = splitShore(
      model,
      profileForSplit,
      c.disciplineId,
      c.channelId,
      c.levelId,
      total,
    );
    const on = split.onshore;
    const near = split.nearshore;
    const off = split.offshore;

    const cfgHubId = profileForSplit.ogs?.disciplines?.find(
      (d) =>
        d.disciplineId === c.disciplineId &&
        (d.channelId == null ||
          d.channelId === 'all' ||
          d.channelId === c.channelId),
    )?.hubId;

    const onCost =
      on *
      (Number(
        lookupCostPerRole(model, 'onshore', c.disciplineId, c.channelId, c.levelId),
      ) || 0);
    const nearCost =
      near *
      (Number(
        lookupCostPerRole(
          model,
          'nearshore',
          c.disciplineId,
          c.channelId,
          c.levelId,
          cfgHubId,
        ),
      ) || 0);
    const offCost =
      off *
      (Number(
        lookupCostPerRole(
          model,
          'offshore',
          c.disciplineId,
          c.channelId,
          c.levelId,
          cfgHubId,
        ),
      ) || 0);

    const key = staffPlanCellKey(c.disciplineId, c.channelId, c.levelId);
    const prev = byKey.get(key);
    if (prev) {
      prev.baseFte += total;
      prev.adjustedFte += total;
      prev.onshoreFte += on;
      prev.nearshoreFte += near;
      prev.offshoreFte += off;
      prev.onshoreCost += onCost;
      prev.nearshoreCost += nearCost;
      prev.offshoreCost += offCost;
      prev.totalCost += onCost + nearCost + offCost;
      prev.postAutomationFte += total;
      continue;
    }
    byKey.set(key, {
      disciplineId: c.disciplineId,
      disciplineLabel: labelForDiscipline(model, c.disciplineId),
      channelId: c.channelId,
      channelLabel: labelForChannel(model, c.channelId),
      levelId: c.levelId,
      levelLabel: exactLevelLabel(c.levelId),
      baseFte: total,
      adjustedFte: total,
      onshoreFte: on,
      nearshoreFte: near,
      offshoreFte: off,
      onshoreCost: onCost,
      nearshoreCost: nearCost,
      offshoreCost: offCost,
      totalCost: onCost + nearCost + offCost,
      sAutoFte: 0,
      postAutomationFte: total,
    });
  }
  return Array.from(byKey.values());
}

/** Materialize an adjusted grid as staff-plan cells, for publishing the
 *  Modeled-Adjusted plan into the client workspace. */
export function basisCellsToStaffPlanCells(
  cells: DisciplineChannelCell[] | undefined,
): StaffPlanCell[] {
  const out: StaffPlanCell[] = [];
  for (const c of cells || []) {
    const on = Number(c.onshoreFte) || 0;
    const hub = (Number(c.nearshoreFte) || 0) + (Number(c.offshoreFte) || 0);
    if (on + hub <= DRIFT_EPS) continue;
    out.push({
      disciplineId: c.disciplineId,
      channelId: c.channelId,
      levelId: c.levelId,
      fte: on + hub,
      onFte: on,
      hubFte: hub,
    });
  }
  return out;
}

export interface StaffPlanTotals {
  /** Headline headcount: modeled cells plus `otherTotal`. */
  total: number;
  /** Onshore headcount, including other roles. */
  onTotal: number;
  /** Hub (nearshore + offshore) headcount, including other roles. */
  hubTotal: number;
  /** Headcount in roles outside the model (`plan.otherRoles`). */
  otherTotal: number;
  /** Per-discipline/level/channel roll-ups cover modeled cells only. */
  byDiscipline: Record<string, number>;
  byLevel: Partial<Record<LevelId, number>>;
  byChannel: Record<string, number>;
  byDisciplineChannel: Record<string, number>;
  byDisciplineLevel: Record<string, number>;
  /** Per-level on/hub split. */
  byLevelSplit: Partial<Record<LevelId, { on: number; hub: number }>>;
}

/** `plan.otherRoles` rolled up per role label (levels summed), largest first. */
export function summarizeOtherRoles(
  plan: Pick<StaffPlan, 'otherRoles'> | undefined | null,
): Array<{ role: string; on: number; hub: number; total: number }> {
  const byRole = new Map<string, { role: string; on: number; hub: number; total: number }>();
  for (const r of plan?.otherRoles || []) {
    const { total, on, hub } = resolveCellSplit(r);
    if (total <= DRIFT_EPS) continue;
    const key = r.role.trim().toLowerCase();
    const cur = byRole.get(key);
    if (cur) {
      cur.on += on;
      cur.hub += hub;
      cur.total += total;
    } else {
      byRole.set(key, { role: r.role.trim(), on, hub, total });
    }
  }
  return [...byRole.values()].sort((a, b) => b.total - a.total);
}

/** Pre-computed roll-ups for the editor + comparison views. */
export function totalsForStaffPlan(plan: StaffPlan | undefined): StaffPlanTotals {
  const out: StaffPlanTotals = {
    total: 0,
    onTotal: 0,
    hubTotal: 0,
    otherTotal: 0,
    byDiscipline: {},
    byLevel: {},
    byChannel: {},
    byDisciplineChannel: {},
    byDisciplineLevel: {},
    byLevelSplit: {},
  };
  for (const r of plan?.otherRoles || []) {
    const { total, on, hub } = resolveCellSplit(r);
    out.total += total;
    out.onTotal += on;
    out.hubTotal += hub;
    out.otherTotal += total;
  }
  for (const c of plan?.cells || []) {
    const { total, on, hub } = resolveCellSplit(c);
    if (!total && !on && !hub) continue;
    out.total += total;
    out.onTotal += on;
    out.hubTotal += hub;
    out.byDiscipline[c.disciplineId] = (out.byDiscipline[c.disciplineId] || 0) + total;
    out.byLevel[c.levelId] = (out.byLevel[c.levelId] || 0) + total;
    out.byChannel[c.channelId] = (out.byChannel[c.channelId] || 0) + total;
    const dc = `${c.disciplineId}::${c.channelId}`;
    out.byDisciplineChannel[dc] = (out.byDisciplineChannel[dc] || 0) + total;
    const dl = `${c.disciplineId}:${c.levelId}`;
    out.byDisciplineLevel[dl] = (out.byDisciplineLevel[dl] || 0) + total;
    const lvSplit = out.byLevelSplit[c.levelId] || { on: 0, hub: 0 };
    lvSplit.on += on;
    lvSplit.hub += hub;
    out.byLevelSplit[c.levelId] = lvSplit;
  }
  return out;
}
